import React from "react";
import { USE_LOCAL_FALLBACK } from "../../config/firebaseConfig";
import {
  uniforms_getInventory,
  uniforms_upsertItem,
  uniforms_adjustStock,
  uniforms_deleteItem,
  uniforms_getBeginningInventory,
  uniforms_upsertBeginningInventory,
  uniforms_getDeliveries,
  uniforms_getSales,
  uniforms_setOnHand
} from "../../lib/firestoreAdapters";
import { useOutletContext } from "react-router-dom";

function invKey(campus) { return `uniforms_inventory_${String(campus || "gen").toLowerCase()}`; }
function loadInv(campus) {
  try { const raw = localStorage.getItem(invKey(campus)); return raw ? JSON.parse(raw) : []; } catch { return []; }
}
function saveInv(campus, data) {
  try { localStorage.setItem(invKey(campus), JSON.stringify(data)); } catch { void 0; }
}
function beginningKey(campus, year) { return `uniforms_beginning_inventory_${String(campus || "gen").toLowerCase()}_${year}`; }
function loadBeginning(campus, year) {
  try { const raw = localStorage.getItem(beginningKey(campus, year)); return raw ? JSON.parse(raw) : []; } catch { return []; }
}
function saveBeginning(campus, year, data) {
  try { localStorage.setItem(beginningKey(campus, year), JSON.stringify(data)); } catch { void 0; }
}
function parseCSV(text) {
  const rows = [];
  let i = 0, field = "", row = [], inQuotes = false;
  while (i < text.length) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else { inQuotes = false; }
      } else {
        field += ch;
      }
    } else {
      if (ch === '"') inQuotes = true;
      else if (ch === ',') { row.push(field.trim()); field = ""; }
      else if (ch === '\n' || ch === '\r') {
        if (field !== "" || row.length) { row.push(field.trim()); rows.push(row); row = []; field = ""; }
        if (ch === '\r' && text[i + 1] === '\n') i++;
      } else {
        field += ch;
      }
    }
    i++;
  }
  if (field !== "" || row.length) { row.push(field.trim()); rows.push(row); }
  if (!rows.length) return [];
  const headers = rows[0].map(s => s.trim());
  return rows.slice(1).filter(r => r.length && r.some(c => String(c).trim() !== "")).map(cols => {
    const rowObj = {};
    headers.forEach((h, idx) => { rowObj[h] = cols[idx] || ""; });
    return rowObj;
  });
}
function normalizeNumber(v) {
  const s = String(v || "").replace(/[^0-9.]/g, "");
  if (!s) return 0;
  const n = Number(s);
  return isNaN(n) ? 0 : n;
}
function slugify(v) {
  return String(v || "").toUpperCase().replace(/[^A-Z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function Inventory() {
  const { campus } = useOutletContext();
  const currentYear = new Date().getFullYear();
  const [rows, setRows] = React.useState(() => loadInv(campus));
  const [beginningYear, setBeginningYear] = React.useState(currentYear);
  const [beginningRows, setBeginningRows] = React.useState([]);
  const [q, setQ] = React.useState("");
  const [editIndex, setEditIndex] = React.useState(-1);
  const [editData, setEditData] = React.useState(null);
  const [importing, setImporting] = React.useState(false);
  const [importInfo, setImportInfo] = React.useState("");
  const [overwriteStock, setOverwriteStock] = React.useState(false);
  const [useSKUKey, setUseSKUKey] = React.useState(true);
  const [syncing, setSyncing] = React.useState(false);
  const [autoSku, setAutoSku] = React.useState(true);
  const [showAdd, setShowAdd] = React.useState(false);
  const [newItem, setNewItem] = React.useState({ product: "", size: "", sku: "", barcode: "", price: "", cost: "", stock: "" });
  const [recomputing, setRecomputing] = React.useState(false);
  React.useEffect(() => {
    async function fetch() {
      if (USE_LOCAL_FALLBACK) {
        setRows(loadInv(campus));
      } else {
        const data = await uniforms_getInventory(campus);
        setRows(data);
      }
    }
    fetch();
  }, [campus]);
  React.useEffect(() => {
    async function fetchBeginning() {
      if (USE_LOCAL_FALLBACK) {
        setBeginningRows(loadBeginning(campus, beginningYear));
      } else {
        try {
          const data = await uniforms_getBeginningInventory(campus, beginningYear);
          setBeginningRows(data);
        } catch {
          setBeginningRows([]);
        }
      }
    }
    fetchBeginning();
  }, [campus, beginningYear]);
  const filtered = React.useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(r =>
      String(r.product || r.Name || "").toLowerCase().includes(s) ||
      String(r.size || r.Size || "").toLowerCase().includes(s)
    );
  }, [rows, q]);
  const beginningMap = React.useMemo(() => {
    const m = new Map();
    for (const r of beginningRows) {
      m.set(`${String(r.product).toLowerCase()}|${String(r.size).toLowerCase()}`, Number(r.beginningQty || 0));
    }
    return m;
  }, [beginningRows]);
  const beginningYears = React.useMemo(() => {
    const years = new Set([currentYear, currentYear - 1]);
    for (const r of beginningRows) {
      if (r?.year) years.add(Number(r.year));
    }
    return Array.from(years).sort((a, b) => b - a);
  }, [beginningRows, currentYear]);

  function rowKey(row) {
    return `${String(row.product).toLowerCase()}|${String(row.size).toLowerCase()}`;
  }
  function getBeginningQty(row) {
    return beginningMap.get(rowKey(row)) ?? 0;
  }
  async function saveBeginningQty(row, qty) {
    const payload = {
      product: row.product,
      size: row.size,
      sku: row.sku || "",
      beginningQty: Number(qty || 0)
    };
    if (USE_LOCAL_FALLBACK) {
      const key = rowKey(row);
      const existing = loadBeginning(campus, beginningYear);
      const next = existing.filter(r => `${String(r.product).toLowerCase()}|${String(r.size).toLowerCase()}` !== key);
      next.push({ ...payload, year: beginningYear, campus });
      setBeginningRows(next);
      saveBeginning(campus, beginningYear, next);
    } else {
      await uniforms_upsertBeginningInventory(campus, beginningYear, payload);
      const data = await uniforms_getBeginningInventory(campus, beginningYear);
      setBeginningRows(data);
    }
  }
  async function useCurrentStockAsBeginning() {
    const confirmed = window.confirm(`Use current on-hand stock as beginning inventory for ${beginningYear}?`);
    if (!confirmed) return;
    if (USE_LOCAL_FALLBACK) {
      const next = rows.map(r => ({
        campus,
        year: beginningYear,
        product: r.product,
        size: r.size,
        sku: r.sku || "",
        beginningQty: Number(r.stock || 0)
      }));
      setBeginningRows(next);
      saveBeginning(campus, beginningYear, next);
    } else {
      for (const row of rows) {
        // eslint-disable-next-line no-await-in-loop
        await uniforms_upsertBeginningInventory(campus, beginningYear, {
          product: row.product,
          size: row.size,
          sku: row.sku || "",
          beginningQty: Number(row.stock || 0)
        });
      }
      const data = await uniforms_getBeginningInventory(campus, beginningYear);
      setBeginningRows(data);
    }
    setImportInfo(`Beginning inventory for ${beginningYear} was updated from current stock.`);
  }

  async function recomputeOnHandFromHistory() {
    const confirmed = window.confirm(`Recompute on-hand stock using Beginning ${beginningYear} + deliveries - sales? This will overwrite current on-hand values for ${campus}.`);
    if (!confirmed) return;
    if (USE_LOCAL_FALLBACK) {
      setImportInfo("Recompute is not available in local fallback mode.");
      return;
    }
    setRecomputing(true);
    try {
      const start = `${beginningYear}-01-01`;
      const end = new Date().toISOString().slice(0, 10);
      const inRange = (d) => {
        const s = String(d || "").slice(0, 10);
        return s >= start && s <= end;
      };
      const expected = new Map();
      for (const r of rows) {
        expected.set(`${String(r.product || "").toLowerCase()}|${String(r.size || "").toLowerCase()}`, 0);
      }
      for (const r of beginningRows) {
        expected.set(`${String(r.product || "").toLowerCase()}|${String(r.size || "").toLowerCase()}`, Number(r.beginningQty || 0));
      }
      const receipts = await uniforms_getDeliveries(campus);
      for (const rec of receipts) {
        if (!inRange(rec.date)) continue;
        for (const it of (rec.items || [])) {
          const key = `${String(it.product || "").toLowerCase()}|${String(it.size || "").toLowerCase()}`;
          expected.set(key, (expected.get(key) || 0) + Number(it.qty || 0));
        }
      }
      const sales = await uniforms_getSales(campus);
      for (const s of sales) {
        if (!inRange(s.date)) continue;
        for (const it of (s.items || [])) {
          const key = `${String(it.product || "").toLowerCase()}|${String(it.size || "").toLowerCase()}`;
          expected.set(key, (expected.get(key) || 0) - Number(it.qty || 0));
        }
      }

      const entries = Array.from(expected.entries());
      const concurrency = 15;
      let done = 0;
      setImportInfo(`Recomputing… 0/${entries.length}`);
      for (let i = 0; i < entries.length; i += concurrency) {
        const batch = entries.slice(i, i + concurrency);
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(batch.map(([key, stock]) => {
          const parts = key.split("|");
          const product = (rows.find(r => `${String(r.product || "").toLowerCase()}|${String(r.size || "").toLowerCase()}` === key)?.product) || parts[0];
          const size = (rows.find(r => `${String(r.product || "").toLowerCase()}|${String(r.size || "").toLowerCase()}` === key)?.size) || parts[1];
          return uniforms_setOnHand(campus, product, size, stock);
        }));
        done += batch.length;
        setImportInfo(`Recomputing… ${done}/${entries.length}`);
      }
      const data = await uniforms_getInventory(campus);
      setRows(data);
      setImportInfo(`On-hand stock was recomputed from ${start} to ${end}.`);
    } catch (e) {
      setImportInfo(`Recompute failed: ${String(e?.code || e?.message || e)}`);
    } finally {
      setRecomputing(false);
    }
  }

  async function onImportFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true); setImportInfo("");
    const reader = new FileReader();
    reader.onload = async () => {
      const text = String(reader.result || "");
      const parsed = parseCSV(text);
      // Normalize common headers
      const mapped = parsed.map(r => ({
        product: String(r.product || r.Name || r["Uniform Name"] || "").trim(),
        size: String(r.size || r.Size || r["Size Code"] || "").trim(),
        price: normalizeNumber(r.price || r.Price || ""),
        cost: normalizeNumber(r.cost || r.Cost || ""),
        stock: normalizeNumber(r.stock || r.Stock || r["On Hand"] || ""),
        sku: String(r.sku || r.SKU || "").trim(),
        barcode: String(r.barcode || r.Barcode || "").trim()
      }));
      if (autoSku) {
        for (const m of mapped) {
          if (!m.sku) {
            const base = slugify(m.product);
            const sz = m.size ? "-" + slugify(m.size) : "";
            m.sku = `${base}${sz}`;
          }
        }
      }
      const valid = mapped.filter(x => x.product && (x.size || x.sku));
      if (USE_LOCAL_FALLBACK) {
        const before = loadInv(campus);
        const { next, created, updated, skipped } = mergeByKeyDetailed(before, valid, { overwriteStock, useSKUKey });
        setRows(next);
        saveInv(campus, next);
        setImportInfo(`Imported ${valid.length} row(s). Created: ${created}, Updated: ${updated}, Skipped: ${skipped}.`);
      } else {
        try {
          let saved = 0;
          let failed = 0;
          let firstError = "";
          const concurrency = 20;
          const existing = await uniforms_getInventory(campus);
          const existMap = {};
          for (const r of existing) {
            const key = (useSKUKey && String(r.sku || "").trim())
              ? String(r.sku).trim().toLowerCase()
              : `${String(r.product).trim().toLowerCase()}|${String(r.size).trim().toLowerCase()}`;
            existMap[key] = r;
          }
          setImportInfo(`Importing… 0/${valid.length}`);
          for (let i = 0; i < valid.length; i += concurrency) {
            const batch = valid.slice(i, i + concurrency).map(it => {
              const key = (useSKUKey && String(it.sku || "").trim())
                ? String(it.sku).trim().toLowerCase()
                : `${String(it.product).trim().toLowerCase()}|${String(it.size).trim().toLowerCase()}`;
              const exist = existMap[key];
              const baseStock = Number(exist?.stock || 0);
              const newStock = overwriteStock ? Number(it.stock || 0) : baseStock + Number(it.stock || 0);
              return { ...it, stock: newStock };
            });
            const results = await Promise.allSettled(batch.map(it => uniforms_upsertItem(campus, it)));
            for (const r of results) {
              if (r.status === "fulfilled") saved++;
              else {
                failed++;
                if (!firstError && r.reason) firstError = String(r.reason.code || r.reason.message || r.reason);
              }
            }
            setImportInfo(`Importing… ${saved + failed}/${valid.length}`);
          }
          const data = await uniforms_getInventory(campus);
          setRows(data);
          if (failed > 0) {
            setImportInfo(`Partial sync: saved ${saved}/${valid.length}, failed ${failed}${firstError ? ` (${firstError})` : ""}. Local data kept; use Retry Sync to push remaining.`);
          } else {
            setImportInfo(`Imported ${saved} row(s).`);
          }
        } catch (_err) {
          const before = loadInv(campus);
          const { next, created, updated, skipped } = mergeByKeyDetailed(before, valid, { overwriteStock, useSKUKey });
          setRows(next);
          saveInv(campus, next);
          const msg = (_err && (_err.message || _err.code)) ? ` (${String(_err.code || _err.message)})` : "";
          setImportInfo(`Network issue${msg}. Saved locally: ${valid.length} row(s). Created: ${created}, Updated: ${updated}, Skipped: ${skipped}.`);
        }
      }
      setImporting(false);
    };
    reader.readAsText(file);
  }
  function mergeByKeyDetailed(existing, imported, opts) {
    const map = {};
    for (const r of existing) {
      const key = (opts.useSKUKey && String(r.sku || "").trim())
        ? String(r.sku).trim().toLowerCase()
        : `${String(r.product).trim().toLowerCase()}|${String(r.size).trim().toLowerCase()}`;
      map[key] = r;
    }
    let created = 0, updated = 0, skipped = 0;
    for (const r of imported) {
      const key = (opts.useSKUKey && String(r.sku || "").trim())
        ? String(r.sku).trim().toLowerCase()
        : `${String(r.product).trim().toLowerCase()}|${String(r.size).trim().toLowerCase()}`;
      if (!key || !r.product || !r.size) { skipped++; continue; }
      if (!map[key]) { map[key] = r; created++; } else {
        // Update price/stock if provided
        const baseStock = Number(map[key].stock || 0);
        const nextStock = opts.overwriteStock ? Number(r.stock || 0) : baseStock + Number(r.stock || 0);
        map[key] = { ...map[key], ...r, stock: nextStock };
        updated++;
      }
    }
    return { next: Object.values(map), created, updated, skipped };
  }
  async function adjustStock(i, delta) {
    if (USE_LOCAL_FALLBACK) {
      const next = rows.slice();
      next[i] = { ...next[i], stock: Number(next[i].stock || 0) + delta };
      setRows(next); saveInv(campus, next);
    } else {
      const it = rows[i];
      await uniforms_adjustStock(campus, it.product, it.size, delta);
      const data = await uniforms_getInventory(campus);
      setRows(data);
    }
  }
  function startEdit(i) {
    setEditIndex(i);
    setEditData(rows[i]);
  }
  function cancelEdit() {
    setEditIndex(-1);
    setEditData(null);
  }
  function onEditChange(field, value) {
    setEditData(prev => ({ ...prev, [field]: value }));
  }
  async function saveEdit() {
    if (!editData) return;
    const payload = {
      product: editData.product || "",
      size: editData.size || "",
      sku: editData.sku || "",
      barcode: editData.barcode || "",
      price: Number(editData.price || 0),
      cost: Number(editData.cost || 0),
      stock: Number(editData.stock || 0),
    };
    if (USE_LOCAL_FALLBACK) {
      let next = rows.slice();
      next[editIndex] = payload;
      // If product/size changed to a key that already exists, merge by key
      const key = `${String(payload.product).toLowerCase()}|${String(payload.size).toLowerCase()}`;
      const firstIdx = next.findIndex(x => `${String(x.product).toLowerCase()}|${String(x.size).toLowerCase()}` === key);
      if (firstIdx !== -1 && firstIdx !== editIndex) {
        next[firstIdx] = { ...next[firstIdx], ...payload };
        next.splice(editIndex, 1);
      }
      setRows(next);
      saveInv(campus, next);
      cancelEdit();
    } else {
      const before = rows[editIndex];
      await uniforms_upsertItem(campus, payload);
      // If product/size changed, try to archive/delete old doc; ignore errors for uniforms role
      if (String(before.product) !== String(payload.product) || String(before.size) !== String(payload.size)) {
        try { await uniforms_deleteItem(campus, before.product, before.size); } catch (e) { /* ignore */ }
      }
      let data = await uniforms_getInventory(campus);
      // As a safety net, hide any stale duplicate by old key from the view even if archiving failed
      data = data.filter(d => !(String(d.product).toLowerCase() === String(before.product).toLowerCase() && String(d.size).toLowerCase() === String(before.size).toLowerCase()));
      setRows(data);
      cancelEdit();
    }
  }
  function openAdd() {
    setNewItem({ product: "", size: "", sku: "", barcode: "", price: "", cost: "", stock: "" });
    setShowAdd(true);
  }
  function closeAdd() {
    setShowAdd(false);
  }
  async function saveAdd() {
    const p = String(newItem.product || "").trim();
    const s = String(newItem.size || "").trim();
    if (!p) { alert("Uniform Name is required"); return; }
    let sku = String(newItem.sku || "").trim();
    if (!sku && autoSku) {
      const base = slugify(p);
      const sz = s ? "-" + slugify(s) : "";
      sku = `${base}${sz}`;
    }
    const payload = {
      product: p,
      size: s,
      sku,
      barcode: String(newItem.barcode || "").trim(),
      price: normalizeNumber(newItem.price),
      cost: normalizeNumber(newItem.cost),
      stock: normalizeNumber(newItem.stock),
    };
    if (USE_LOCAL_FALLBACK) {
      const { next } = mergeByKeyDetailed(rows, [payload], { overwriteStock: false, useSKUKey });
      setRows(next);
      saveInv(campus, next);
      setShowAdd(false);
    } else {
      try {
        await uniforms_upsertItem(campus, payload);
        const data = await uniforms_getInventory(campus);
        setRows(data);
        setShowAdd(false);
      } catch (e) {
        const { next } = mergeByKeyDetailed(rows, [payload], { overwriteStock: false, useSKUKey });
        setRows(next);
        saveInv(campus, next);
        setShowAdd(false);
        setImportInfo("Network issue while adding. Saved locally; use Retry Sync to push.");
      }
    }
  }

  return (
    <div>
      {USE_LOCAL_FALLBACK && (
        <div className="alert alert-warning">
          Development mode: using local browser storage. In production this will use Firestore.
        </div>
      )}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Inventory — {campus.toUpperCase()}</h5>
        <div className="d-flex gap-2">
          <input className="form-control" style={{ width: 260 }} placeholder="Search name or size" value={q} onChange={e => setQ(e.target.value)} />
          <input type="file" accept=".csv" className="form-control" onChange={onImportFile} title="Import CSV (Name, Size, Price, Cost, Stock, SKU, Barcode)" />
          <button className="btn btn-primary" onClick={openAdd}>Add Uniform</button>
          <div className="form-check form-check-inline">
            <input className="form-check-input" type="checkbox" id="useSKUKey" checked={useSKUKey} onChange={e => setUseSKUKey(e.target.checked)} />
            <label className="form-check-label" htmlFor="useSKUKey">Use SKU as key</label>
          </div>
          <div className="form-check form-check-inline">
            <input className="form-check-input" type="checkbox" id="overwriteStock" checked={overwriteStock} onChange={e => setOverwriteStock(e.target.checked)} />
            <label className="form-check-label" htmlFor="overwriteStock">Overwrite On Hand</label>
          </div>
          <div className="form-check form-check-inline">
            <input className="form-check-input" type="checkbox" id="autoSku" checked={autoSku} onChange={e => setAutoSku(e.target.checked)} />
            <label className="form-check-label" htmlFor="autoSku">Auto-generate SKU</label>
          </div>
          {importing ? <span className="text-muted">{importInfo || "Importing…"}</span> : (importInfo ? <span className="text-success">{importInfo}</span> : null)}
          {!USE_LOCAL_FALLBACK && importInfo.startsWith("Network issue") && (
            <button
              className="btn btn-outline-primary btn-sm"
              disabled={syncing}
              onClick={async () => {
                setSyncing(true);
                try {
                  const before = rows; // local merged rows for this campus
                  const concurrency = 20;
                  let pushed = 0;
                  for (let i = 0; i < before.length; i += concurrency) {
                    const batch = before.slice(i, i + concurrency);
                    // eslint-disable-next-line no-await-in-loop
                    await Promise.all(batch.map(it => uniforms_upsertItem(campus, it)));
                    pushed += batch.length;
                    setImportInfo(`Syncing… ${pushed}/${before.length}`);
                  }
                  const data = await uniforms_getInventory(campus);
                  setRows(data);
                  setImportInfo(`Synced ${pushed} item(s) to Firestore.`);
                } catch (_e) {
                  setImportInfo("Sync failed. Will keep local data; try again later.");
                } finally {
                  setSyncing(false);
                }
              }}
            >
              Retry Sync to Firestore
            </button>
          )}
        </div>
      </div>
      <div className="card mb-3">
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <div className="fw-bold">Beginning Inventory Setup</div>
              <div className="text-muted small">
                Record opening balances by year so inventory and sales reports can show historical totals.
              </div>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <label className="small text-muted mb-0">Year</label>
              <select
                className="form-select"
                style={{ width: 120 }}
                value={beginningYear}
                onChange={e => setBeginningYear(Number(e.target.value))}
              >
                {beginningYears.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <button className="btn btn-outline-primary" onClick={useCurrentStockAsBeginning}>
                Use Current Stock
              </button>
              <button className="btn btn-outline-secondary" disabled={recomputing} onClick={recomputeOnHandFromHistory}>
                {recomputing ? "Recomputing…" : "Recompute On-hand"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showAdd && (
        <div className="card mb-3">
          <div className="card-header">Add Uniform</div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4">
                <input className="form-control" placeholder="Uniform Name" value={newItem.product} onChange={e => setNewItem({ ...newItem, product: e.target.value })} />
              </div>
              <div className="col-md-2">
                <input className="form-control" placeholder="Size" value={newItem.size} onChange={e => setNewItem({ ...newItem, size: e.target.value })} />
              </div>
              <div className="col-md-3">
                <input className="form-control" placeholder="SKU" value={newItem.sku} onChange={e => setNewItem({ ...newItem, sku: e.target.value })} />
              </div>
              <div className="col-md-3">
                <input className="form-control" placeholder="Barcode" value={newItem.barcode} onChange={e => setNewItem({ ...newItem, barcode: e.target.value })} />
              </div>
              <div className="col-md-2">
                <input className="form-control" placeholder="Price" value={newItem.price} onChange={e => setNewItem({ ...newItem, price: e.target.value })} />
              </div>
              <div className="col-md-2">
                <input className="form-control" placeholder="Cost" value={newItem.cost} onChange={e => setNewItem({ ...newItem, cost: e.target.value })} />
              </div>
              <div className="col-md-2">
                <input className="form-control" placeholder="On Hand" value={newItem.stock} onChange={e => setNewItem({ ...newItem, stock: e.target.value })} />
              </div>
            </div>
          </div>
          <div className="card-footer d-flex justify-content-end gap-2">
            <button className="btn btn-secondary" onClick={closeAdd}>Cancel</button>
            <button className="btn btn-success" onClick={saveAdd}>Save</button>
          </div>
        </div>
      )}
      <div className="table-responsive">
        <table className="table table-bordered">
          <thead className="table-light">
            <tr>
              <th>Uniform Name</th>
              <th>Size</th>
              <th>SKU</th>
              <th>Barcode</th>
              <th className="text-end">Price</th>
              <th className="text-end">Cost</th>
              <th className="text-end">Beginning {beginningYear}</th>
              <th className="text-end">On Hand</th>
              <th className="text-center">Adjust</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="9" className="text-center text-muted">No inventory yet. Import a CSV to seed.</td></tr>
            ) : filtered.map((r, i) => (
              <tr key={i}>
                <td>{editIndex === i ? (<input className="form-control" value={editData.product} onChange={e => onEditChange("product", e.target.value)} />) : r.product}</td>
                <td>{editIndex === i ? (<input className="form-control" value={editData.size} onChange={e => onEditChange("size", e.target.value)} />) : r.size}</td>
                <td>{editIndex === i ? (<input className="form-control" value={editData.sku || ""} onChange={e => onEditChange("sku", e.target.value)} />) : (r.sku || "-")}</td>
                <td>{editIndex === i ? (<input className="form-control" value={editData.barcode || ""} onChange={e => onEditChange("barcode", e.target.value)} />) : (r.barcode || "-")}</td>
                <td className="text-end">{editIndex === i ? (<input type="number" className="form-control text-end" value={editData.price ?? 0} onChange={e => onEditChange("price", e.target.value)} />) : <>₱{Number(r.price || 0).toLocaleString()}</>}</td>
                <td className="text-end">{editIndex === i ? (<input type="number" className="form-control text-end" value={editData.cost ?? 0} onChange={e => onEditChange("cost", e.target.value)} />) : <>₱{Number(r.cost || 0).toLocaleString()}</>}</td>
                <td className="text-end">
                  <input
                    type="number"
                    className="form-control text-end"
                    value={getBeginningQty(r)}
                    onChange={e => saveBeginningQty(r, e.target.value)}
                  />
                </td>
                <td className="text-end">{editIndex === i ? (<input type="number" className="form-control text-end" value={editData.stock ?? 0} onChange={e => onEditChange("stock", e.target.value)} />) : Number(r.stock || 0)}</td>
                <td className="text-center">
                  {editIndex === i ? (
                    <div className="btn-group btn-group-sm">
                      <button className="btn btn-primary" onClick={saveEdit}>Save</button>
                      <button className="btn btn-outline-secondary" onClick={cancelEdit}>Cancel</button>
                    </div>
                  ) : (
                    <div className="btn-group btn-group-sm">
                      <button className="btn btn-outline-secondary" onClick={() => adjustStock(i, -1)}>-1</button>
                      <button className="btn btn-outline-secondary" onClick={() => adjustStock(i, 1)}>+1</button>
                      <button className="btn btn-outline-primary" onClick={() => startEdit(i)}>Edit</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
